import type { TableGridView as TableGridViewType } from '@gitbook/doc-core';
import { PaletteCommandType, type PaletteDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { DEFAULT_TABLE_COLUMN_WIDTH } from '../../../common';
import type {
    TableDefinitionViewCommandsParams,
    TableViewCommandsParams,
    TableViewSpec,
} from '../types';
import { pickColumnDefinitions } from '../utils';
import { GridDefinitionSettings } from './GridDefinitionSettings';
import { onKeyDown } from './keyboard';
import { TableGridView } from './TableGridView';

export const gridSpec: TableViewSpec<TableGridViewType> = {
    icon: Icon.Table,
    title: 'Table',
    recordName: 'row',
    definitionName: 'column',
    selectableView: true,
    ViewComponent: TableGridView,
    DefinitionSettingsComponent: GridDefinitionSettings,
    commands: getGridViewCommands,
    definitionCommands: getGridViewDefinitionCommands,
    onKeyDown,
};

function getGridViewDefinitionCommands(
    params: TableDefinitionViewCommandsParams<TableGridViewType>
): PaletteGroupDef[] {
    const { view, definition, setView } = params;
    const enabled = typeof view.columnWidths?.[definition.id] === 'undefined';

    return [
        {
            key: 'grid',
            commands: [
                {
                    key: 'grid.autoSize',
                    title: 'Auto size',
                    icon: Icon.AutoSize,
                    type: PaletteCommandType.SWITCH,
                    enabled,
                    trigger: () => {
                        if (!enabled) {
                            const { [definition.id]: id, ...otherWidths } = view.columnWidths || {};

                            setView({
                                ...view,
                                useNewSizing: true,
                                columnWidths: otherWidths,
                            });
                        } else {
                            setView({
                                ...view,
                                useNewSizing: true,
                                columnWidths: {
                                    ...view.columnWidths,
                                    [definition.id]: DEFAULT_TABLE_COLUMN_WIDTH,
                                },
                            });
                        }
                    },
                },
            ],
        },
    ];
}

/**
 * Get the commands to control the view.
 */
function getGridViewCommands(
    params: TableViewCommandsParams<TableGridViewType>
): PaletteGroupDef[] {
    const { view, setView } = params;

    return [
        {
            key: 'grid',
            title: 'Table layout',
            commands: [
                {
                    key: 'grid.toggleHeader',
                    title: 'Show header',
                    type: PaletteCommandType.SWITCH,
                    enabled: !view.hideHeader,
                    icon: Icon.Heading,
                    trigger: () => {
                        const [createPalette] = setView({
                            ...view,
                            hideHeader: !view.hideHeader,
                        });

                        return createPalette();
                    },
                },
                {
                    key: 'grid.columns',
                    title: 'Visible columns',
                    icon: Icon.Eye,
                    descendant: () => getToggleColumnCommands(params),
                },
                {
                    key: 'grid.resetSizing',
                    title: 'Reset column sizing',
                    icon: Icon.ResetColumnSizing,
                    trigger: () => {
                        setView({
                            ...view,
                            columnWidths: {},
                            useNewSizing: true,
                        });
                    },
                },
            ],
        },
    ];
}

/** Create commands to toggle columns */
function getToggleColumnCommands(params: TableViewCommandsParams<TableGridViewType>): PaletteDef {
    const { view, definition, setView } = params;

    return pickColumnDefinitions(
        definition,
        (selected) => {
            const [, newParams] = setView({
                ...view,
                columns: selected,
            });

            return getToggleColumnCommands(newParams);
        },
        {
            multiple: true,
            search: 'Select visible columns',
            selected: view.columns,
        }
    );
}
